import { type TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { CardPackCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';

import { commonPacksContainerQuery } from 'components/commonCard/CommonPacksPage/utils';

import { BasicPacks } from '../BasicPacks';
import { PremiumPack } from '../PremiumPack';
import { StarPack } from '../StarPack';
import { ScrollableHighlightedPacks } from './ScrollableHighlightedPacks';
import type { CardPackGroup_cardPackGroup } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  gap: var(--double-unit);
  @container ${commonPacksContainerQuery} {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: var(--triple-unit);
  }
  @container (min-width: 1000px) {
    grid-template-columns: calc(42 * var(--unit)) minmax(0, 1fr) calc(
        42 * var(--unit)
      );
  }
`;

const splitCardPacksByType = (
  cardPacks: CardPackGroup_cardPackGroup['cardPacks']
) => {
  const result = {
    starPack: null as CardPackGroup_cardPackGroup['cardPacks'][number] | null,
    basicPacks: [] as CardPackGroup_cardPackGroup['cardPacks'][number][],
    premiumPack: null as
      | CardPackGroup_cardPackGroup['cardPacks'][number]
      | null,
  };

  cardPacks.forEach(pack => {
    if (pack.rewardedByTask) {
      result.starPack = pack;
    } else if (pack.currency === CardPackCurrency.COMMON_GEM) {
      result.premiumPack = pack;
    } else {
      result.basicPacks.push(pack);
    }
  });

  return result;
};

type Props = {
  cardPackGroup: CardPackGroup_cardPackGroup;
  onClickBuy: (cardPackGroupId: string, cardPackSlug: string) => void;
  isWideContainer: boolean;
};
export const CardPackGroup = ({
  cardPackGroup,
  onClickBuy,
  isWideContainer,
}: Props) => {
  const { cardPacks } = cardPackGroup;
  const { premiumPack, basicPacks, starPack } = splitCardPacksByType(cardPacks);

  return (
    <Root>
      {isWideContainer ? (
        premiumPack && (
          <PremiumPack
            cardPack={premiumPack}
            cardPackGroup={cardPackGroup}
            onClickBuy={onClickBuy}
          />
        )
      ) : (
        <ScrollableHighlightedPacks
          starCardPack={starPack}
          premiumCardPack={premiumPack}
          cardPackGroup={cardPackGroup}
          onClickBuy={onClickBuy}
        />
      )}
      <BasicPacks
        cardPacks={basicPacks}
        cardPackGroupId={cardPackGroup.id}
        onClickBuy={onClickBuy}
      />
      {!!starPack && !!isWideContainer && <StarPack cardPack={starPack} />}
    </Root>
  );
};

CardPackGroup.fragments = {
  cardPackGroup: gql`
    fragment CardPackGroup_cardPackGroup on CardPackGroup {
      id
      cardPacks {
        slug
        currency
        ...PremiumPack_cardPack
        rewardedByTask {
          id
        }
        ...StarPack_cardPack
        ...ScrollableHighlightedPacks_cardPack
        ...PremiumPack_cardPack
      }
      ...PremiumPack_cardPackGroup
    }
    ${PremiumPack.fragments.cardPackGroup}
    ${PremiumPack.fragments.cardPack}
    ${StarPack.fragments.cardPack}
    ${ScrollableHighlightedPacks.fragments.cardPack}
  ` as TypedDocumentNode<CardPackGroup_cardPackGroup>,
};
